import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import Loader from "@amzn/meridian/loader";
import Button from "@amzn/meridian/button";
import { FormattedMessage, injectIntl, intlShape } from "react-intl";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import TaskAttributes from "./TaskAttributes";
import TaskSOP from "./TaskSOP";
import {
  getTaskById,
  updateTaskStatus,
  addNoteForTask,
} from "../../apis/TaskApi";
import { TASK_STATUS, TASK_TYPE } from "../../utils/Constants";
import TaskResolution from "./resolution/TaskResolution";
import { isGcaWebFeatureEnabledFromParent } from "../../handlers/ParentDataHandler";
import { logOperationalMetric } from "../../utils/Helper";
import { useDeviceLayout } from "../common/context/DeviceLayoutContext";

//For NA EU Task Name is AMZLStations_F2F_Coaching_Alert and for FE AMZLStations_F2F_Coaching_Alert_JP
const GCA_TASK_NAME = "AMZLStations_F2F_Coaching_Alert";
const FRAMELESS_SUFFIX = "?frameless";

function TaskDetails({ intl }) {
  const [isGCA, setGCA] = useState(false);
  const [isCompletionButtonDisabled, setIsCompletionButtonDisabled] =
    useState(false);
  const [showResolutionQuestions, setShowResolutionQuestions] = useState(false);
  const [answerForIssuesFaced, setAnswerForIssuesFaced] = useState();
  const [answerForTaskType, setAnswerForTaskType] = useState();
  const [resolutionComments, setResolutionComments] = useState("");
  const [resolutionQuestionsAnswered, setResolutionQuestionsAnswered] =
    useState(false);
  const taskId = decodeURIComponent(useParams().taskId);
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [task, setTask] = useState({});
  const [gcaOldView, setGcaOldView] = useState(false);
  const [getTaskTimeStamp, setGetTaskTimeStamp] = useState(null);
  const processName = "WorkItem_Details";
  const { spacingInset_shorthand2 } = useDeviceLayout();

  /*********************
   *                   *
   *        hooks      *
   *                   *
   *********************/

  useEffect(() => {
    getTask(taskId);
  }, []);

  /*********************
   *                   *
   *      handlers     *
   *                   *
   *********************/
  const onCancel = () => {
    history.push(`/overview`);
  };
  const onCompleteTask = () => {
    resolveTask();
  };

  /*********************
   *                   *
   *        api        *
   *                   *
   *********************/

  const isTaskGCA = (task_name) => {
    return (
      task_name.data.task.hasOwnProperty("name") &&
      task_name.data.task.name.startsWith(GCA_TASK_NAME)
    );
  };

  const getTask = () => {
    setIsLoading(true);
    const timestamp = Date.now();
    setGetTaskTimeStamp(timestamp);
    getTaskById(taskId)
      .then((res) => {
        if (res.status === 200) {
          console.log(res.data.task.name);
          const metricName = res.data.task.name;
          logOperationalMetric(
            getTaskTimeStamp,
            processName,
            metricName,
            true,
            true
          );
          if (isTaskGCA(res)) {
            isGcaWebFeatureEnabledFromParent()
              .then((res) => {
                setGCA(res.isGcaWebEnabled);
                logOperationalMetric(
                  getTaskTimeStamp,
                  processName,
                  "GCA_NewView",
                  true,
                  true
                );
              })
              .catch((error) => {
                setGCA(false);
                console.error("Error getting value for GCA feature", error);
              });
          } else if (
            res.data.task.hasOwnProperty("type") &&
            res.data.task.type === TASK_TYPE.HELP_REQUEST
          ) {
            setShowResolutionQuestions(true);
          }
          console.log(res.data.task);
          setTask(res.data.task);
          setIsCompletionButtonDisabled(
            !!res.data.task.isManualResolutionDisabled
          );
        } else {
          //todo Error handling
        }
      })
      .catch((err) => {
        console.log(err);
        //todo Error handling
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getGCAUrl = (task) => {
    return task.actionButton.url + FRAMELESS_SUFFIX;
  };

  const getGCAOldView = () => {
    setGcaOldView(true);
    const metricName = "GCA_OldView";
    if (getTaskTimeStamp) {
      logOperationalMetric(
        getTaskTimeStamp,
        processName,
        metricName,
        true,
        true
      );
    }
  };

  const resolveTask = () => {
    if (isLoading) return false;
    setIsLoading(true);
    updateTaskStatus(taskId, TASK_STATUS.RESOLVED)
      .then((res) => {
        if (res.status === 200) {
          if (!showResolutionQuestions) {
            history.push(`/overview`);
          } else {
            submitNotesAndRedirect();
          }
        } else {
          console.log(res);
          //todo Error handling
        }
      })
      .catch((err) => {
        console.log(err);
        //todo Error handling
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const submitNotesAndRedirect = () => {
    if (isLoading) return false;
    setIsLoading(true);
    const metadataMap = {
      issues: answerForIssuesFaced,
      taskType: answerForTaskType,
    };
    addNoteForTask(taskId, resolutionComments, metadataMap)
      .then((res) => {
        if (res.status !== 200) {
          console.error("Received non-200 status");
        }
      })
      .catch((err) => {
        console.error("Error in adding notes for the task", err);
      })
      .finally(() => {
        // re-direct the user despite failures
        history.push(`/overview`);
        setIsLoading(false);
      });
  };

  /*********************
   *                   *
   *  UI components    *
   *                   *
   *********************/

  const renderCompletionButton = () => {
    if (isGCA) {
      return (
        <Button size="small" disabled={true}>
          <FormattedMessage
            id="taskDetails.button.completeGCATask"
            defaultMessage="Complete in Umbrella Coaching Instance"
          />
        </Button>
      );
    }
    return (
      <Button
        size="small"
        onClick={onCompleteTask}
        disabled={
          isCompletionButtonDisabled ||
          (showResolutionQuestions ? !resolutionQuestionsAnswered : false)
        }
      >
        <FormattedMessage
          id="taskDetails.button.completeTask"
          defaultMessage="Complete Task"
        />
      </Button>
    );
  };

  const renderResolutionForm = () => {
    return (
      <TaskResolution
        task={task}
        answerForIssuesFaced={answerForIssuesFaced}
        setAnswerForIssuesFaced={setAnswerForIssuesFaced}
        answerForRequestType={answerForTaskType}
        setAnswerForTaskType={setAnswerForTaskType}
        resolutionComments={resolutionComments}
        setResolutionComments={setResolutionComments}
        setResolutionQuestionsAnswered={setResolutionQuestionsAnswered}
      />
    );
  };

  /*********************
   *                   *
   *    renderer       *
   *                   *
   *********************/

  if (isLoading || !task.id)
    return (
      <div style={{ display: "flex", justifyContent: "space-around" }}>
        <Loader />
      </div>
    );

  return (
    <Column spacingInset={spacingInset_shorthand2}>
      {isGCA && !gcaOldView ? (
        <Row
          width="100%"
          wrap="down"
          alignmentVertical="top"
          allow="fullscreen"
        >
          <Button onClick={getGCAOldView}>
            <FormattedMessage
              id="taskDetails.button.GcaOldView"
              defaultMessage="Go to Old view"
            />
          </Button>
          <Column height="100vh" width="100%">
            <iframe
              title="GCA_iFrame"
              height="100%"
              width="100%"
              style={{ overflow: "auto" }}
              src={getGCAUrl(task)}
            ></iframe>
          </Column>
        </Row>
      ) : (
        <React.Fragment>
          <Row
            wrap="down"
            widths="100%"
            maxWidth="900px"
            alignmentVertical="top"
          >
            <Column alignmentHorizontal="start">
              <TaskSOP task={task} />
            </Column>
            <TaskAttributes task={task} />
            {showResolutionQuestions ? renderResolutionForm(intl) : null}
          </Row>
          <div>
            {/* Complete & Cancel Buttons */}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <div style={{ marginRight: "1rem" }}>
                <Button size="small" type="secondary" onClick={onCancel}>
                  <FormattedMessage
                    id="taskDetails.button.cancel"
                    defaultMessage="Cancel"
                  />
                </Button>
              </div>
              {renderCompletionButton()}
            </div>
          </div>
        </React.Fragment>
      )}
    </Column>
  );
}

TaskDetails.propTypes = {
  intl: intlShape.isRequired,
};

export default injectIntl(TaskDetails);



// WEBPACK FOOTER //
// ./src/components/taskExecution/TaskDetails.jsx